import React from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useElementWidth } from '../hooks/useElementWidth';
import { formatNumberValue } from '../utils/format';

export interface SparklinePoint {
  key: string;
  /** Names the point in the hover readout, e.g. "Jul 26". */
  label: string;
  value: number | null;
  /** Not final yet — drawn dashed with a hollow mark. */
  provisional?: boolean;
}

export interface SparklineProps {
  points: SparklinePoint[];
  /** A number of px, or `fill` to take the width of its container. */
  width?: number | 'fill';
  height?: number;
  /** Below this many values there is no line worth drawing. */
  minPoints?: number;
  formatValue?: (value: number) => string;
  /** What the line shows, for the readout and screen readers. */
  label?: string;
  /** Said after a provisional point's label in the readout. */
  provisionalLabel?: string;
  /** The background it sits on, so hollow marks are filled to match. */
  surface?: 'raised' | 'sunken';
}

const PAD = 3;

/**
 * A small trend line with a readout for every point.
 *
 * A quiet line, a mark on each point while they have room to stand apart, and
 * the brand colour on the latest point — the one that matters. Hovering shows
 * a crosshair and the point's label and value, marks or no marks.
 *
 * Points that are not final yet (a cohort still settling, a month in
 * progress) are dashed with hollow marks, so how much of the line is a real
 * reading shows at a glance.
 *
 * For a trend inside a card or a table cell. Not a chart: no axes, no scale.
 */
export function Sparkline({
  points,
  width = 'fill',
  height = 24,
  minPoints = 3,
  formatValue = formatNumberValue,
  label,
  provisionalLabel = 'still settling',
  surface = 'raised',
}: SparklineProps) {
  const theme = useTheme();
  const [measureRef, measured] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = React.useState<number | null>(null);

  const w = width === 'fill' ? Math.floor(measured) : width;
  const usable = points.filter((p) => p.value !== null);
  const drawable = usable.length >= minPoints && w > 0;

  const frame = (content: React.ReactNode) => (
    <Box ref={measureRef} sx={{ width: width === 'fill' ? '100%' : width, height, minWidth: 0 }}>
      {content}
    </Box>
  );
  if (!drawable) return frame(null);

  const values = usable.map((p) => p.value as number);
  const lo = Math.min(...values);
  const span = Math.max(...values) - lo || 1;
  const x = (i: number) => (points.length < 2 ? w / 2 : PAD + (i / (points.length - 1)) * (w - 2 * PAD));
  const y = (v: number) => height - PAD - ((v - lo) / span) * (height - 2 * PAD);

  const path = (from: number, to: number) =>
    points
      .slice(from, to)
      .map((p, k) => (p.value === null ? null : `${k ? 'L' : 'M'}${x(from + k).toFixed(1)} ${y(p.value).toFixed(1)}`))
      .filter(Boolean)
      .join(' ');

  const firstProvisional = points.findIndex((p) => p.provisional);
  const cut = firstProvisional < 0 ? points.length : firstProvisional;
  const settledPath = path(0, cut);
  // Overlap by one point so the dashed run joins the solid one.
  const provisionalPath = cut < points.length ? path(Math.max(0, cut - 1), points.length) : '';

  const lastIndex = points.length - 1 - [...points].reverse().findIndex((p) => p.value !== null);
  const last = points[lastIndex];
  const slot = w / points.length;
  // A mark is ~3.6px across, so past a couple of dozen points in a table cell
  // the marks touch and the line reads as a caterpillar rather than a trend.
  // Drop them when the points sit closer than a mark's width plus a gap: the
  // line still shows the movement, the dashed run still shows what is still
  // settling, and hovering still works — the hit targets are separate.
  const showMarks = slot >= 8;
  const stroke = showMarks ? 1.4 : 1.1;

  const line = theme.tokens.text.tertiary;
  const accent = theme.tokens.brand.primary;
  const hole = surface === 'sunken' ? theme.tokens.surface.sunken : theme.tokens.surface.raised;

  const hovered = hover !== null ? points[hover] : null;
  const readout =
    hovered && hovered.value !== null ? (
      <Box sx={{ py: 0.25 }}>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
          {hovered.label}
          {hovered.provisional ? ` · ${provisionalLabel}` : ''}
        </Typography>
        <Typography variant="body2" sx={{ display: 'block', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {formatValue(hovered.value)}
        </Typography>
      </Box>
    ) : (
      label ?? ''
    );

  const first = usable[0];
  const summary = label
    ? `${label}: ${formatValue(first.value as number)} in ${first.label} to ${formatValue(last.value as number)} in ${last.label}`
    : 'Trend';

  return frame(
    <Tooltip title={readout} followCursor placement="top">
      <Box
        component="svg"
        width={w}
        height={height}
        viewBox={`0 0 ${w} ${height}`}
        sx={{ display: 'block', overflow: 'visible', cursor: 'crosshair' }}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={summary}
      >
        {settledPath && (
          <path d={settledPath} fill="none" stroke={line} strokeWidth={stroke} strokeLinejoin="round" strokeLinecap="round" />
        )}
        {provisionalPath && (
          <path
            d={provisionalPath}
            fill="none"
            stroke={line}
            strokeWidth={stroke}
            strokeDasharray="2.5 2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {showMarks && points.map((p, i) =>
          p.value === null || i === lastIndex ? null : (
            <circle
              key={`mark-${p.key}`}
              cx={x(i)}
              cy={y(p.value)}
              r={1.8}
              fill={p.provisional ? hole : line}
              stroke={line}
              strokeWidth={p.provisional ? 1 : 0}
            />
          ),
        )}

        {last && last.value !== null && (
          <circle
            cx={x(lastIndex)}
            cy={y(last.value)}
            r={2.4}
            fill={last.provisional ? hole : accent}
            stroke={accent}
            strokeWidth={last.provisional ? 1.2 : 0}
          />
        )}

        {hovered && hovered.value !== null && hover !== null && (
          <>
            <line x1={x(hover)} y1={0} x2={x(hover)} y2={height} stroke={theme.tokens.border.strong} strokeWidth={1} />
            <circle cx={x(hover)} cy={y(hovered.value)} r={3} fill={accent} />
          </>
        )}

        {/* Hit targets a full slot wide and the full height; the marks
            themselves are a few pixels and nearly impossible to land on. */}
        {points.map((p, i) =>
          p.value === null ? null : (
            <rect
              key={`hit-${p.key}`}
              x={x(i) - slot / 2}
              y={0}
              width={slot}
              height={height}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          ),
        )}
      </Box>
    </Tooltip>,
  );
}
